/**
 * Email bot/security scanner detection utilities.
 * 
 * Email security gateways (Barracuda, Proofpoint, Microsoft ATP, Mimecast, etc.)
 * routinely pre-fetch images (triggering open events) and click ALL links in
 * emails to scan for malware.  These look like real engagement but inflate
 * open/click metrics.
 *
 * Detection layers:
 *  1. Server-side `isMachineOpen` flag (set by server/emailMarketing.js detectMachineOpen)
 *  2. Bot user-agent patterns (real mail clients — Gmail/Yahoo proxies, Outlook, Apple Mail — are allowed)
 *  3. Truncated browser UA (gateway fingerprint)
 *  4. Open burst: 3+ opens from same recipient within 60s
 *  5. Click burst: 4+ unique URLs from same recipient within 120s
 *  6. Bounced-email exclusion (false positives from MTAs that open/click before bouncing)
 *  7. Clicks-without-opens within 2 min of delivery → scanner clicks (later ones are humans with images off)
 */

// Safe millis extraction — handles Firestore Timestamps, Date objects, and epoch numbers
function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (typeof ts.toMillis === 'function') return ts.toMillis(); // Firestore Timestamp
  if (typeof ts.getTime === 'function') return ts.getTime();   // Date object
  return 0;
}

// ── User-Agent detection ────────────────────────────────────────────────

const BOT_UA_PATTERNS = [
  'python-requests', 'python-urllib', 'go-http-client',
  'curl/', 'wget', 'java/', 'libwww-perl',
  'headlesschrome', 'phantomjs',
  'bot', 'crawler', 'spider', 'scan', 'check',
  'barracuda', 'mimecast', 'proofpoint', 'fireeye', 'trellix',
  'xtpt/',  // X-Trace-Proxy-Tag — email security proxy fingerprint
];

/**
 * Real mail clients whose image fetches only happen when a person opens the message.
 * Keep in sync with isKnownMailClientUA() in server/emailMarketing.js.
 */
export function isKnownMailClientUA(ua) {
  if (!ua) return false;
  const l = ua.toLowerCase();
  if (l.includes('googleimageproxy') || l.includes('yahoomailproxy')) return true;
  if (l.includes('ms-office') || l.includes('msoffice') || l.includes('microsoft outlook') || l.includes('outlook-ios') || l.includes('outlook-android')) return true;
  if (/\((iphone|ipad|macintosh);/.test(l) && l.includes('applewebkit/605.1.15 (khtml, like gecko)') &&
      !l.includes('safari/') && !l.includes('chrome/')) return true;
  return false;
}

/**
 * Returns true if the user-agent string looks like a bot or security scanner.
 *
 * NOTE: A "known gateway Chrome version" fingerprint (e.g. flagging Chrome/109 as an
 * always-Barracuda signature) was removed here in September 2026 after it produced a
 * confirmed false positive — a real recipient on an older-but-legitimate managed/corporate
 * Chrome build was misclassified as a bot, suppressing a genuine open + click. Pinned
 * browser versions age out of being gateway-specific and start matching ordinary users
 * faster than this list can be kept updated; the other checks below (explicit bot
 * substrings, truncated/generic UA, missing UA, burst timing) are far more reliable.
 */
export function isBotUserAgent(ua) {
  if (!ua) return false;
  if (isKnownMailClientUA(ua)) return false;
  const lower = ua.toLowerCase();

  // Known bot / automation user-agents
  if (BOT_UA_PATTERNS.some(p => lower.includes(p))) return true;

  // Truncated WebKit UA — real browsers ALWAYS include a version token like
  // "Chrome/125.0.0.0" or "Safari/537.36".  Security gateways copy only
  // the platform prefix. (Apple Mail is allowed above.)
  if (
    lower.includes('applewebkit') &&
    lower.includes('khtml') &&
    !lower.includes('chrome/') &&
    !lower.includes('safari/') &&
    !lower.includes('firefox/') &&
    !lower.includes('edg/') &&
    !lower.includes('opr/') &&
    !lower.includes('vivaldi/')
  ) {
    return true;
  }

  return false;
}

// ── Burst detection helpers ─────────────────────────────────────────────

/**
 * Detect recipients whose opens look like a security scanner.
 * Returns a Set of email addresses flagged as open-burst bots.
 *
 * Heuristic: 3+ opens within 60 seconds from the same recipient.
 */
export function detectOpenBurstEmails(events) {
  const burstEmails = new Set();
  const opensByRecipient = {};

  events.forEach(event => {
    if (event.eventType === 'opened' && event.recipientEmail) {
      const email = event.recipientEmail;
      if (!opensByRecipient[email]) opensByRecipient[email] = [];
      opensByRecipient[email].push(toMillis(event.timestamp));
    }
  });

  Object.entries(opensByRecipient).forEach(([email, timestamps]) => {
    if (timestamps.length < 3) return;
    timestamps.sort((a, b) => a - b);
    for (let i = 0; i <= timestamps.length - 3; i++) {
      if (timestamps[i + 2] - timestamps[i] <= 60000) {
        burstEmails.add(email);
        break;
      }
    }
  });

  return burstEmails;
}

/**
 * Detect recipients whose clicks look like a security scanner.
 * Returns a Set of email addresses flagged as click-burst bots.
 *
 * Heuristic: 4+ unique URLs clicked within 120 seconds.
 */
export function detectClickBurstEmails(events) {
  const burstEmails = new Set();
  const clicksByRecipient = {};

  events.forEach(event => {
    if (event.eventType === 'clicked' && event.recipientEmail) {
      const email = event.recipientEmail;
      if (!clicksByRecipient[email]) clicksByRecipient[email] = [];
      clicksByRecipient[email].push({
        time: toMillis(event.timestamp),
        url: event.metadata?.clickedUrl || '',
      });
    }
  });

  Object.entries(clicksByRecipient).forEach(([email, clicks]) => {
    if (clicks.length < 4) return;
    clicks.sort((a, b) => a.time - b.time);
    for (let i = 0; i <= clicks.length - 4; i++) {
      const windowEnd = clicks[i].time + 120000;
      const windowClicks = clicks.filter(
        c => c.time >= clicks[i].time && c.time <= windowEnd
      );
      const uniqueUrls = new Set(windowClicks.map(c => c.url));
      if (uniqueUrls.size >= 4) {
        burstEmails.add(email);
        break;
      }
    }
  });

  return burstEmails;
}

// ── Composite filter ────────────────────────────────────────────────────

/**
 * Drop BCC/verification copies from campaign events. An address on the BCC list that was
 * also an actual To recipient (has its own `sent` event) keeps its events — the sender
 * skips the BCC copy for that address, so those events are the real recipient's.
 */
export function filterAudienceEvents(events, bccEmailSet = new Set()) {
  const realRecipients = new Set();
  events.forEach(e => {
    if (e.eventType === 'sent' && e.recipientEmail && e.isBccCopy !== true) {
      realRecipients.add(`${e.campaignId}|${e.recipientEmail.toLowerCase()}`);
    }
  });
  return events.filter(e => {
    const email = e.recipientEmail?.toLowerCase();
    if (!email || realRecipients.has(`${e.campaignId}|${email}`)) return true;
    return e.isBccCopy !== true && !bccEmailSet.has(email);
  });
}

const SCANNER_CLICK_WINDOW_MS = 2 * 60 * 1000;

/**
 * Build a composite "is human engagement" filter for a set of email events.
 *
 * Returns `{ isHumanEngagement, botEmails }`:
 *  - isHumanEngagement(event) → boolean
 *  - botEmails: Set<string> of emails that are flagged as bot/bounced
 */
export function buildEngagementFilter(events) {
  // Bounced emails → exclude all their opens/clicks as false positives
  const bouncedEmails = new Set();
  events.forEach(e => {
    if (e.eventType === 'bounced' && e.recipientEmail) bouncedEmails.add(e.recipientEmail);
  });

  const openBurstEmails = detectOpenBurstEmails(events);
  const clickBurstEmails = detectClickBurstEmails(events);

  // ── Phase 1: classify opens to find recipients with genuine human opens ──
  // An open is "human" if it passes all open-specific checks.
  const humanOpenRecipients = new Set();
  events.forEach(event => {
    if (event.eventType !== 'opened') return;
    if (bouncedEmails.has(event.recipientEmail)) return;
    if (event.isMachineOpen) return;
    if (event.metadata?.isMachineOpen) return;
    if (openBurstEmails.has(event.recipientEmail)) return;
    if (clickBurstEmails.has(event.recipientEmail)) return;
    if (isBotUserAgent(event.metadata?.userAgent)) return;
    humanOpenRecipients.add(event.recipientEmail);
  });

  // ── Phase 2: clicks with no human open, landing right after delivery, are scanner clicks ──
  // Clicks later than that are humans with images blocked (Outlook's default), so they count.
  const deliveredAt = {};
  events.forEach(event => {
    if ((event.eventType === 'delivered' || event.eventType === 'sent') && event.recipientEmail) {
      const t = toMillis(event.timestamp);
      const prev = deliveredAt[event.recipientEmail];
      if (t && (event.eventType === 'delivered' || !prev)) deliveredAt[event.recipientEmail] = t;
    }
  });
  const clickWithoutOpenEmails = new Set();
  events.forEach(event => {
    if (event.eventType !== 'clicked' || !event.recipientEmail ||
        humanOpenRecipients.has(event.recipientEmail)) return;
    const base = deliveredAt[event.recipientEmail];
    if (!base || toMillis(event.timestamp) - base <= SCANNER_CLICK_WINDOW_MS) {
      clickWithoutOpenEmails.add(event.recipientEmail);
    }
  });

  const botEmails = new Set([...openBurstEmails, ...clickBurstEmails, ...clickWithoutOpenEmails]);

  /**
   * Returns true only if the event represents genuine human engagement.
   */
  const isHumanEngagement = (event) => {
    if (bouncedEmails.has(event.recipientEmail)) return false;
    if (event.isMachineOpen) return false;
    if (event.metadata?.isMachineOpen) return false;
    if (openBurstEmails.has(event.recipientEmail) &&
        (event.eventType === 'opened' || event.eventType === 'clicked')) return false;
    if (clickBurstEmails.has(event.recipientEmail) &&
        (event.eventType === 'opened' || event.eventType === 'clicked')) return false;
    if (isBotUserAgent(event.metadata?.userAgent)) return false;
    // Clicks from recipients with zero human opens are bot clicks
    if (event.eventType === 'clicked' && clickWithoutOpenEmails.has(event.recipientEmail)) return false;
    return true;
  };

  return { isHumanEngagement, bouncedEmails, botEmails, openBurstEmails, clickBurstEmails, clickWithoutOpenEmails };
}
